/**
 * 背面カメラの中から「最も広角」のレンズを選び、`navigator.mediaDevices.getUserMedia`
 * を monkey-patch して 8th Wall がそのレンズを使うようにする。
 *
 * iPhone Pro 系には複数の背面レンズ（Ultra Wide / Wide / Telephoto）があり、
 * デフォルトでは Wide (1x, FOV ~73°) が選ばれる。Ultra Wide (0.5x, FOV ~120°)
 * を強制することで AR の視野を広げる。
 */

type LensInfo = {
  deviceId: string;
  label: string;
  rank: number; // 大きいほど広角優先
};

let preferredDeviceId: string | null = null;
let patched = false;
let trackerInstalled = false;
let cachedSetup: { picked: LensInfo | null; candidates: LensInfo[] } | null = null;
const activeStreams = new Set<MediaStream>();

/** 8th Wall が開いた MediaStream をすべて停止する。AR 終了時に呼ぶこと。 */
export function stopAllCameraStreams(): void {
  for (const stream of activeStreams) {
    stream.getTracks().forEach((t) => {
      try {
        t.stop();
      } catch {
        // ignore
      }
    });
  }
  activeStreams.clear();
}

/**
 * getUserMedia を wrap して、開いたストリームを追跡する（停止して回るため）。
 * deviceId は変更しないので iOS のデフォルトカメラ選択を尊重する。
 * setupWidestBackCamera() を使わない場合のクリーンアップ用。
 */
export function installStreamTracker(): void {
  if (trackerInstalled || patched) return;
  trackerInstalled = true;
  if (!navigator.mediaDevices?.getUserMedia) return;
  const original = navigator.mediaDevices.getUserMedia.bind(navigator.mediaDevices);
  navigator.mediaDevices.getUserMedia = async (constraints?: MediaStreamConstraints) => {
    const stream = await original(constraints);
    if (constraints?.video) {
      activeStreams.add(stream);
      stream.getTracks().forEach((t) => {
        t.addEventListener('ended', () => activeStreams.delete(stream));
      });
    }
    return stream;
  };
}

function rankLabel(label: string): number {
  // 数値が大きいほど優先度が高い。
  // 標準 1x の単独レンズを最優先（SLAM 安定のため固定レンズが望ましい）。
  // iOS の「背面カメラ」(無修飾) ＝ 標準広角 1x。
  const norm = label.trim();
  if (/^背面カメラ$|^back camera$/i.test(norm)) return 100;
  // 望遠 (Telephoto) は最低
  if (/望遠|tele|2x|3x|5x|10x/i.test(label)) return 0;
  // 超広角は次点
  if (/超広角|ultra.?wide|0\.5/i.test(label)) return 70;
  // 個別の広角レンズ
  if (/広角|wide.?angle|wide|1x/i.test(label) && !/超広角/.test(label)) return 60;
  // 複合カメラ（トリプル/デュアル）は SLAM でレンズ切替が起きうるので避けたい
  if (/トリプル|triple|デュアル|dual/i.test(label)) return 20;
  if (/背面|back|environment/i.test(label)) return 30;
  return 10;
}

export async function setupWidestBackCamera(): Promise<{
  picked: LensInfo | null;
  candidates: LensInfo[];
}> {
  // 一度成功したらキャッシュ。warmup の getUserMedia 呼び出しを再実行するのは
  // iOS でカメラ獲得競合の原因になる。
  if (cachedSetup) {
    installGetUserMediaPatch();
    return cachedSetup;
  }

  if (!navigator.mediaDevices?.enumerateDevices || !navigator.mediaDevices.getUserMedia) {
    return { picked: null, candidates: [] };
  }

  // ラベルを取得するには一度カメラ権限が必要。ダミー stream をすぐ閉じる。
  let warmup: MediaStream | null = null;
  try {
    warmup = await navigator.mediaDevices.getUserMedia({
      video: { facingMode: 'environment' },
      audio: false,
    });
  } catch (e) {
    console.warn('[camera-selector] warmup permission failed', e);
    return { picked: null, candidates: [] };
  } finally {
    warmup?.getTracks().forEach((t) => t.stop());
  }

  const devices = await navigator.mediaDevices.enumerateDevices();
  const candidates: LensInfo[] = devices
    .filter((d) => d.kind === 'videoinput')
    .map((d) => ({
      deviceId: d.deviceId,
      label: d.label || '(no label)',
      rank: rankLabel(d.label || ''),
    }))
    .sort((a, b) => b.rank - a.rank);

  console.log('[camera-selector] candidates:', candidates);

  const picked = candidates[0] ?? null;
  if (picked) {
    preferredDeviceId = picked.deviceId;
    installGetUserMediaPatch();
  }
  cachedSetup = { picked, candidates };
  return cachedSetup;
}

function installGetUserMediaPatch(): void {
  if (patched) return;
  patched = true;
  const original = navigator.mediaDevices.getUserMedia.bind(navigator.mediaDevices);
  navigator.mediaDevices.getUserMedia = async (constraints?: MediaStreamConstraints) => {
    if (preferredDeviceId && constraints?.video) {
      const videoConstraints =
        typeof constraints.video === 'boolean' ? {} : { ...constraints.video };
      (videoConstraints as MediaTrackConstraints).deviceId = { exact: preferredDeviceId };
      // facingMode と deviceId は競合しうるので facingMode を取り除く
      delete (videoConstraints as MediaTrackConstraints).facingMode;
      constraints = { ...constraints, video: videoConstraints };
      console.log('[camera-selector] patched getUserMedia → deviceId', preferredDeviceId);
    }
    const stream = await original(constraints);
    if (constraints?.video) {
      activeStreams.add(stream);
      // ストリーム終了時に追跡から外す
      stream.getTracks().forEach((t) => {
        t.addEventListener('ended', () => activeStreams.delete(stream));
      });
    }
    return stream;
  };
}
